// ReviewerSelectionPopup.tsx - Updated with GatePassForm Design System
import React, { useState, useEffect, useCallback } from 'react';
import './ApproverReviewerPopup.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://gatepass-api-1753632520.azurewebsites.net/api';

interface Company {
  companyId: number;
  companyName: string;
  companyDescription?: string;
  fileId?: string;
  regionName?: string;
  regionCode?: string;
}

interface User {
  userId: number;
  userName: string;
  userEmail: string;
}

interface FunctionWithApproversReviewers {
  functionId: number;
  functionName: string;
  functionDescription: string;
  companyId: number;
  companyName: string;
  approvers: User[];
  reviewers: User[];
}

interface ReviewerSelection {
  company: Company | null;
  function: FunctionWithApproversReviewers | null;
  selectedReviewers: User[];
}

interface StatusMessage {
  type: 'success' | 'error' | null;
  message: string;
}

type StepType = 'company' | 'function' | 'users';

interface ReviewerSelectionPopupProps {
  isOpen: boolean;
  currentSelection: ReviewerSelection | null;
  selectedRegionId: number | null;
  getAccessToken: () => Promise<string>;
  onClose: () => void;
  onConfirm: (selection: ReviewerSelection) => void;
  onStatusUpdate: (status: StatusMessage) => void;
}

// SVG Icons for empty states
const CompanyIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg 
    className={className} 
    width="48" 
    height="48" 
    viewBox="0 0 48 48" 
    fill="none" 
    xmlns="http://www.w3.org/2000/svg"
  >
    <path 
      d="M8 42V12C8 10.8954 8.89543 10 10 10H20V6C20 4.89543 20.8954 4 22 4H26C27.1046 4 28 4.89543 28 6V10H38C39.1046 10 40 10.8954 40 12V42C40 43.1046 39.1046 44 38 44H10C8.89543 44 8 43.1046 8 42ZM12 14V40H16V14H12ZM20 14V40H24V14H20ZM28 14V40H32V14H28ZM36 14V40H36V14ZM24 8H24V10H24V8Z" 
      fill="currentColor"
      opacity="0.4"
    />
    <rect x="12" y="16" width="4" height="4" fill="currentColor" opacity="0.6"/>
    <rect x="20" y="16" width="4" height="4" fill="currentColor" opacity="0.6"/>
    <rect x="28" y="16" width="4" height="4" fill="currentColor" opacity="0.6"/>
    <rect x="12" y="24" width="4" height="4" fill="currentColor" opacity="0.6"/>
    <rect x="20" y="24" width="4" height="4" fill="currentColor" opacity="0.6"/>
    <rect x="28" y="24" width="4" height="4" fill="currentColor" opacity="0.6"/>
  </svg>
);

const FunctionIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg 
    className={className} 
    width="48" 
    height="48" 
    viewBox="0 0 48 48" 
    fill="none" 
    xmlns="http://www.w3.org/2000/svg"
  >
    <path 
      d="M20 8H28C29.1046 8 30 8.89543 30 10V12H34C35.1046 12 36 12.8954 36 14V36C36 37.1046 35.1046 38 34 38H14C12.8954 38 12 37.1046 12 36V14C12 12.8954 12.8954 12 14 12H18V10C18 8.89543 18.8954 8 20 8ZM20 12H28V10H20V12ZM24 18C25.1046 18 26 18.8954 26 20C26 21.1046 25.1046 22 24 22C22.8954 22 22 21.1046 22 20C22 18.8954 22.8954 18 24 18ZM20 26H28V24H20V26ZM20 30H28V28H20V30Z" 
      fill="currentColor"
      opacity="0.4"
    />
  </svg>
);

const ReviewerIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg 
    className={className} 
    width="48" 
    height="48" 
    viewBox="0 0 48 48" 
    fill="none" 
    xmlns="http://www.w3.org/2000/svg"
  >
    <path 
      d="M24 4C28.4183 4 32 7.58172 32 12C32 16.4183 28.4183 20 24 20C19.5817 20 16 16.4183 16 12C16 7.58172 19.5817 4 24 4ZM24 24C30.6274 24 36 29.3726 36 36V40C36 42.2091 34.2091 44 32 44H16C13.7909 44 12 42.2091 12 40V36C12 29.3726 17.3726 24 24 24Z" 
      fill="currentColor"
      opacity="0.4"
    />
    <path 
      d="M38 18L40 20L46 14" 
      stroke="currentColor" 
      strokeWidth="2" 
      strokeLinecap="round" 
      strokeLinejoin="round"
      opacity="0.6"
    />
  </svg>
);

export const ReviewerSelectionPopup: React.FC<ReviewerSelectionPopupProps> = ({
  isOpen,
  currentSelection,
  selectedRegionId,
  getAccessToken,
  onClose,
  onConfirm,
  onStatusUpdate
}) => {
  const [step, setStep] = useState<StepType>('company');
  const [showHelp, setShowHelp] = useState(true);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [functions, setFunctions] = useState<FunctionWithApproversReviewers[]>([]);
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  const [selectedFunction, setSelectedFunction] = useState<FunctionWithApproversReviewers | null>(null);
  const [selectedReviewers, setSelectedReviewers] = useState<User[]>([]);
  
  const [isLoadingCompanies, setIsLoadingCompanies] = useState(false);
  const [isLoadingFunctions, setIsLoadingFunctions] = useState(false);
  const [companySearchTerm, setCompanySearchTerm] = useState('');
  const [functionSearchTerm, setFunctionSearchTerm] = useState('');

  // Initialize with current selection when popup opens
  useEffect(() => {
    if (isOpen && currentSelection) {
      setSelectedCompany(currentSelection.company);
      setSelectedFunction(currentSelection.function);
      setSelectedReviewers(currentSelection.selectedReviewers || []);
      
      if (currentSelection.company && currentSelection.function) {
        setStep('users' as StepType);
      } else if (currentSelection.company) {
        setStep('function' as StepType);
      } else {
        setStep('company' as StepType);
      }
    } else if (isOpen) {
      setStep('company' as StepType);
      setSelectedCompany(null);
      setSelectedFunction(null);
      setSelectedReviewers([]);
      setCompanySearchTerm('');
      setFunctionSearchTerm('');
    }
  }, [isOpen, currentSelection]);

  // Lock body scroll when popup is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const loadCompanies = useCallback(async () => {
    // Default to region ID 3 (AsiaPacific) if not specified
    const regionId = selectedRegionId || 3;

    setIsLoadingCompanies(true);
    
    try {
      const accessToken = await getAccessToken();
      const response = await fetch(`${API_BASE_URL}/companies/region/${regionId}`, {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setCompanies(data.data || []);
        
        if (data.data.length === 0) {
          onStatusUpdate({
            type: 'error',
            message: 'No companies found with file access for the selected region.'
          });
        } 
      } else {
        onStatusUpdate({
          type: 'error',
          message: data.message || 'Failed to load companies for the selected region'
        });
      }
    } catch (error) {
      console.error('Error loading companies:', error);
      onStatusUpdate({
        type: 'error',
        message: 'Failed to load companies. Please try again.'
      });
    } finally {
      setIsLoadingCompanies(false);
    }
  }, [getAccessToken, selectedRegionId, onStatusUpdate]);

  const loadFunctions = useCallback(async (companyId: number) => {
    setIsLoadingFunctions(true);
    
    try {
      const accessToken = await getAccessToken();
      const response = await fetch(`${API_BASE_URL}/functions-by-company/${companyId}/gatepass`, {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setFunctions(data.data || []);
        
        if ((data.data || []).length === 0) {
          onStatusUpdate({
            type: 'error',
            message: 'No Divisions Found for this company.'
          });
        }
      } else {
        onStatusUpdate({
          type: 'error',
          message: data.message || 'Failed to load functions'
        });
      }
    } catch (error) {
      console.error('Error loading functions:', error);
      onStatusUpdate({
        type: 'error',
        message: 'Failed to load divisions. Please try again.'
      });
    } finally {
      setIsLoadingFunctions(false);
    }
  }, [getAccessToken, onStatusUpdate]);

  useEffect(() => {
    if (isOpen && step === 'company') {
      loadCompanies();
    }
  }, [isOpen, step, loadCompanies]);

  useEffect(() => {
    if (selectedCompany && step === 'function') {
      loadFunctions(selectedCompany.companyId);
    }
  }, [selectedCompany, step, loadFunctions]);

  const handleCompanySelect = (company: Company) => {
    setSelectedCompany(company);
    setSelectedFunction(null);
    setSelectedReviewers([]);
    setStep('function' as StepType);
  };

  const handleFunctionSelect = (func: FunctionWithApproversReviewers) => {
    setSelectedFunction(func);
    setSelectedReviewers([]);
    setStep('users' as StepType);
  };

  const handleReviewerToggle = (reviewer: User) => {
    setSelectedReviewers(prev => {
      const isSelected = prev.some(r => r.userId === reviewer.userId);
      if (isSelected) {
        return prev.filter(r => r.userId !== reviewer.userId);
      } else {
        return [...prev, reviewer];
      }
    });
  };

  const handleClearReviewers = () => {
    setSelectedReviewers([]);
  };

  const handleConfirm = () => {
    const selection: ReviewerSelection = {
      company: selectedCompany,
      function: selectedFunction,
      selectedReviewers
    };

    onConfirm(selection);
  };

  const filteredCompanies = companies.filter(company =>
    company.companyName.toLowerCase().includes(companySearchTerm.toLowerCase())
  );

  const filteredFunctions = functions.filter(func =>
    func.functionName.toLowerCase().includes(functionSearchTerm.toLowerCase())
  );

  const getStepStatus = (currentStep: StepType, targetStep: StepType): 'current' | 'completed' | 'pending' => {
    const stepOrder: StepType[] = ['company', 'function', 'users'];
    const currentIndex = stepOrder.indexOf(currentStep);
    const targetIndex = stepOrder.indexOf(targetStep);
    
    if (targetIndex < currentIndex) return 'completed';
    if (targetIndex === currentIndex) return 'current';
    return 'pending';
  };

  if (!isOpen) return null;

  return (
    <div className="popup-overlay" onClick={onClose}>
      <div className="popup-container gatepass-style" onClick={(e) => e.stopPropagation()}>
        <div className="popup-header">
          <div className="popup-title-section">
            <span className="popup-title-icon">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#F3694A" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                <circle cx="9" cy="7" r="4"/>
                <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
              </svg>
            </span>
            <h3>Select Reviewers</h3>
          </div>
          <button onClick={onClose} className="popup-close-btn" title="Close">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        <div style={{ display: 'flex', flexDirection: 'row', flex: 1, minHeight: 0, overflow: 'hidden' }}>
          <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <div className="popup-content reviewer-popup-inner">
          {/* Step Indicator - GatePass Style */}
          <div className="progress-indicator">
            <div className="progress-steps">
              {/* Step 1 */}
              <div className="progress-step">
                <div className={`step-circle ${getStepStatus(step, 'company')}`}>
                  1
                </div>
                <div className={`step-connector ${getStepStatus(step, 'function') === 'completed' || getStepStatus(step, 'function') === 'current' ? 'completed' : 'pending'}`} />
              </div>
              
              {/* Step 2 */}
              <div className="progress-step">
                <div className={`step-circle ${getStepStatus(step, 'function')}`}>
                  2
                </div>
                <div className={`step-connector ${getStepStatus(step, 'users') === 'current' ? 'completed' : 'pending'}`} />
              </div>
              
              {/* Step 3 */}
              <div className="progress-step">
                <div className={`step-circle ${getStepStatus(step, 'users')}`}>
                  3
                </div>
              </div>
            </div>
            <div className="progress-info">
              <h3>
                {step === 'company' && 'Select Company'}
                {step === 'function' && 'Choose Division'}
                {step === 'users' && 'Assign Reviewers'}
              </h3>
              <p>
                {step === 'company' && 'Choose a company with access to the selected region'}
                {step === 'function' && 'Select the division to send for review'}
                {step === 'users' && 'Optionally assign reviewers for this request'}
              </p>
            </div>
          </div>

          {/* Step 1: Company Selection */}
          {step === 'company' && (
            <div className="step-content">
              <div className="selection-section">
                <div className="selection-header">
                  <h3>
                    Available Companies
                    <span className="selection-count-badge">
                      {filteredCompanies.length} found
                    </span>
                  </h3>
                </div>

                {/* Info message */}
                <div className="form-info">
                  <span className="info-icon">ℹ️</span>
                  <span className="info-text">
                    Showing only companies active in the selected region
                  </span>
                </div>

                <div className="search-container">
                  <div className="search-input-wrapper">
                    <svg className="search-icon-svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                    </svg>
                    <input
                      type="text"
                      placeholder="Search companies..."
                      value={companySearchTerm}
                      onChange={(e) => setCompanySearchTerm(e.target.value)}
                      className="field-input"
                    />
                  </div>
                </div>

                {isLoadingCompanies ? (
                  <div className="selection-empty">
                    <div className="loading-state">
                      <div className="spinner"></div>
                      <p>Loading companies...</p>
                    </div>
                  </div>
                ) : filteredCompanies.length === 0 ? (
                  <div className="selection-empty">
                    <CompanyIcon className="empty-icon" />
                    <p className="empty-message">No Companies Available</p>
                    <p className="empty-description">
                      No companies have file access configured for the selected region
                    </p>
                  </div>
                ) : (
                  <div className="selected-items-table">
                    <table className="selection-table">
                      <thead>
                        <tr>
                          <th>Company Name</th>
                          <th>Description</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredCompanies.map((company) => (
                          <tr 
                            key={company.companyId}
                            className={selectedCompany?.companyId === company.companyId ? 'selected' : ''}
                            onClick={() => handleCompanySelect(company)}
                          >
                            <td className="company-name">
                              <strong>{company.companyName}</strong>
                            </td>
                            <td className="company-description">
                              {company.companyDescription || 'No description'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Step 2: Function Selection */}
          {step === 'function' && selectedCompany && (
            <div className="step-content">
              <div className="step-header">
                <button
                  onClick={() => setStep('company' as StepType)}
                  className="prev-btn"
                >
                  ← Back to Companies
                </button>
                <h4>{selectedCompany.companyName}</h4>
              </div>

              <div className="selection-section">
                <div className="selection-header">
                  <h3>
                    Available Divisions
                    <span className="selection-count-badge">
                      {filteredFunctions.length} found
                    </span>
                  </h3>
                </div>

                <div className="search-container">
                  <div className="search-input-wrapper">
                    <svg className="search-icon-svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                    </svg>
                    <input
                      type="text"
                      placeholder="Search divisions..."
                      value={functionSearchTerm}
                      onChange={(e) => setFunctionSearchTerm(e.target.value)}
                      className="field-input"
                    />
                  </div>
                </div>

                {isLoadingFunctions ? (
                  <div className="selection-empty">
                    <div className="loading-state">
                      <div className="spinner"></div>
                      <p>Loading Divisions...</p>
                    </div>
                  </div>
                ) : filteredFunctions.length === 0 ? (
                  <div className="selection-empty">
                    <FunctionIcon className="empty-icon" />
                    <p className="empty-message">No Divisions Found</p>
                    <p className="empty-description">
                      This company has no functions configured
                    </p>
                  </div>
                ) : (
                  <div className="selected-items-table">
                    <table className="selection-table">
                      <thead>
                        <tr>
                          <th>Division Name</th>
                          <th>Reviewers</th>
                          <th>Description</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredFunctions.map((func) => (
                          <tr 
                            key={func.functionId}
                            className={selectedFunction?.functionId === func.functionId ? 'selected' : ''}
                            onClick={() => handleFunctionSelect(func)}
                          >
                            <td className="function-name">
                              <strong>{func.functionName}</strong>
                            </td>
                            <td className="function-reviewers">
  {func.reviewers.length > 0 ? (
    <span className="approver-count-badge">
      {func.reviewers.length}
    </span>
  ) : (
    <span className="approver-count-badge no-approvers">
      0
    </span>
  )}
</td>
                            <td className="function-description">
                              {func.functionDescription || 'No description'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Step 3: Reviewer Selection */}
          {step === 'users' && selectedFunction && (
            <div className="step-content">
              <div className="step-header">
                <button
                  onClick={() => setStep('function' as StepType)}
                  className="prev-btn"
                >
                  ← Back to Divisions
                </button>
                <h4>{selectedFunction.functionName}</h4>
              </div>

              <div className="selection-section">
                <div className="selection-header">
                  <h3>
                    Select Reviewers (Optional)
                    <span className="selection-count-badge optional">
                      {selectedReviewers.length} selected
                    </span>
                  </h3>
                </div>

                <div className="selected-items-table">
                  <table className="selection-table">
                    <thead>
                      <tr>
                        <th>Reviewer Name</th>
                        <th>Email</th>
                        <th>Select</th>
                      </tr>
                    </thead>
                    <tbody>
                      {/* No Reviewers Option */}
                      <tr 
                        className={selectedReviewers.length === 0 ? 'selected' : ''}
                        onClick={handleClearReviewers}
                      >
                        <td className="reviewer-name">
                          <strong>No Reviewers</strong>
                          <div className="reviewer-description-small">Skip reviewer assignment</div>
                        </td>
                        <td className="reviewer-email">
                          Optional step
                        </td>
                        <td className="reviewer-selection">
                          <input
                            type="radio"
                            checked={selectedReviewers.length === 0}
                            onChange={handleClearReviewers}
                            onClick={(e) => e.stopPropagation()}
                            className="selection-radio"
                          />
                        </td>
                      </tr>

                      {/* Available Reviewers */}
                      {selectedFunction.reviewers.length === 0 ? (
                        <tr>
                          <td colSpan={3} className="no-reviewers-message">
                            <div className="selection-empty compact">
                              <ReviewerIcon className="empty-icon small" />
                              <p className="empty-message small">No reviewers configured</p>
                              <p className="empty-description small">
                                This function has no reviewers configured
                              </p>
                            </div>
                          </td>
                        </tr>
                      ) : (
                        selectedFunction.reviewers.map((reviewer) => {
                          const isSelected = selectedReviewers.some(r => r.userId === reviewer.userId);
                          return (
                            <tr 
                              key={reviewer.userId}
                              className={isSelected ? 'selected' : ''}
                              onClick={() => handleReviewerToggle(reviewer)}
                            >
                              <td className="reviewer-name">
                                <strong>{reviewer.userName}</strong>
                                
                              </td>
                              <td className="reviewer-email">
                                {reviewer.userEmail}
                              </td>
                              <td className="reviewer-selection">
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={() => handleReviewerToggle(reviewer)}
                                  onClick={(e) => e.stopPropagation()}
                                  className="selection-checkbox"
                                />
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>

                {selectedReviewers.length > 0 && (
                  <div className="form-success">
                    <span className="success-icon" style={{ fontSize: '14px', color: '#6b7280' }}>✓</span>
                    <span className="success-text">
                      <strong>{selectedReviewers.length} reviewer(s) selected:</strong> {selectedReviewers.map(r => r.userName).join(', ')}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="popup-footer">
          <div className="footer-info">
            {step === 'users' && selectedReviewers.length > 0 && (
              <span className="selection-count has-selection">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
                {selectedReviewers.length} reviewer{selectedReviewers.length !== 1 ? 's' : ''} selected
              </span>
            )}
          </div>
          <div className="footer-actions">
            <button onClick={onClose} className="cancel-btn">Cancel</button>
            {step === 'users' && (
              <button onClick={handleConfirm} className="next-btn">
                Confirm Reviewers
              </button>
            )}
          </div>
        </div>
          </div>{/* end left column */}

          {/* Help Panel — animated */}
          <div style={{
            width: showHelp ? 280 : 38,
            minWidth: showHelp ? 280 : 38,
            background: '#fafbfc',
            borderLeft: '1px solid #e5e7eb',
            overflow: 'hidden',
            position: 'relative',
            flexShrink: 0,
            display: 'flex',
            flexDirection: 'column',
            transition: 'width 0.3s ease, min-width 0.3s ease'
          }}>
            <div
              onClick={() => setShowHelp(!showHelp)}
              title={showHelp ? 'Hide Guide' : 'Show Guide'}
              style={{
                position: 'absolute', top: 0, right: 0, bottom: 0, width: 32,
                background: '#f3f4f6', borderLeft: '1px solid #e5e7eb',
                display: 'flex', flexDirection: 'column', alignItems: 'center',
                justifyContent: 'flex-start', paddingTop: 14, gap: 6,
                cursor: 'pointer', color: '#9ca3af', zIndex: 5
              }}
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                {showHelp
                  ? <polyline points="9 18 15 12 9 6"/>
                  : <polyline points="15 18 9 12 15 6"/>
                }
              </svg>
              <span style={{
                writingMode: 'vertical-rl', textOrientation: 'mixed',
                fontSize: 10, fontWeight: 600, letterSpacing: '0.02em',
                whiteSpace: 'nowrap', fontFamily: "'Segoe UI', sans-serif"
              }}>{showHelp ? 'Hide Guide' : 'Show Guide'}</span>
            </div>
            <div style={{
              padding: '20px 50px 20px 18px',
              overflowY: 'auto',
              boxSizing: 'border-box' as const,
              minWidth: 280,
              opacity: showHelp ? 1 : 0,
              transition: 'opacity 0.2s ease'
            }}>
              <div className="help-panel-card help-active">
                <h4 className="help-card-title">Select Reviewers</h4>
                <p className="help-card-description">Start by selecting your contracting company, then choose the division related to your work, and finally pick the reviewers who will provide feedback on your gate pass request.</p>
                <div className="help-card-tip">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#F3694A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: '1px' }}>
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                    <circle cx="12" cy="12" r="3"/>
                  </svg>
                  <span>Reviewers provide an optional quality check — they can endorse or flag concerns but cannot make final approval decisions.</span>
                </div>
              </div>
              <div className="help-panel-quickref">
                <h4 className="help-quickref-title">Quick Reference</h4>
                <ul className="help-quickref-list">
                  <li><span className="help-quickref-bullet"></span><span>Step 1: Select your contracting company</span></li>
                  <li><span className="help-quickref-bullet"></span><span>Step 2: Choose the division related to your work</span></li>
                  <li><span className="help-quickref-bullet"></span><span>Step 3: Pick one or more reviewers</span></li>
                  <li><span className="help-quickref-bullet"></span><span>Reviewers are optional but recommended</span></li>
                </ul>
              </div>
            </div>
          </div>
        </div>{/* end flex row */}
      </div>
    </div>
  );
};